<template>
    <div
        :class="[
            'up-dev-tools',
            {
                'up-dev-tools--development': isDevelopmentEnvironment,
                'up-dev-tools--staging': isStagingEnvironment,
            },
        ]"
    >
        <div
            :class="[
                'up-dev-tools__environment',
                {
                    'up-dev-tools__environment--development': isDevelopmentEnvironment,
                    'up-dev-tools__environment--staging': isStagingEnvironment,
                },
            ]"
        >
            <span class="up-dev-tools__environment-title">Translation DevTools</span>
            <span class="up-dev-tools__environment-value">{{ currentEnvironment }}</span>
        </div>
        <div class="up-dev-tools__urls">
            <div class="up-dev-tools__urls-item">
                <span>Swagger UI</span>
                <a
                    target="_blank"
                    :href="translationSwaggerUrl"
                    >{{ translationSwaggerUrl }}</a
                >
            </div>
        </div>
        <div
            v-if="currentEnvironment"
            class="up-dev-tools__sync"
        >
            <button
                class="up-dev-tools__sync-button"
                type="button"
                :disabled="isSyncing"
                @click="onSyncTranslationsClick"
            >
                <pbl-icon
                    name="arrow-repeat"
                    :class="{ 'up-dev-tools__sync-icon--spinning': isSyncing }"
                />
                Sync {{ currentEnvironmentLabel }}
            </button>
        </div>
        <div class="up-dev-tools__quick-links">
            <button
                class="up-dev-tools__quick-links-button"
                type="button"
                @click="onCopyTranslationTokenClick"
            >
                <pbl-icon name="clipboard-check" />
                Copy Translation Token for Current Use-Case
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { usePebbleToasts } from '@/composables/pebble/usePebbleToasts'
import { isDevelopmentEnvironment, isStagingEnvironment } from '@/utils/environment'
import { useMainStore } from '@/store/mainStore'
import { COMMON_SCOPES, sdk } from '@/authentication/coodeSdkConfig'
import { TranslationService } from '@/api/services/TranslationService'

const rawEnvironment = (import.meta.env.VITE_COODE_ENVIRONMENT ?? '').toLowerCase()
const currentEnvironment = rawEnvironment.toUpperCase()
const currentEnvironmentLabel = (rawEnvironment === 'development' ? 'dev' : rawEnvironment).toUpperCase()

const translationSwaggerUrl = import.meta.env.VITE_TRANSLATION_SERVICE_SWAGGER_URL

const toasts = usePebbleToasts()
const mainStore = useMainStore()

const isSyncing = computed(() => mainStore.isTranslationSyncing)

const onSyncTranslationsClick = async () => {
    if (!currentEnvironmentLabel || isSyncing.value) {
        return
    }

    const environment = currentEnvironmentLabel
    
    try {
        mainStore.isTranslationSyncing = true
        
        const translationService = new TranslationService()

        await translationService.sync()
        await mainStore.initializeTranslation()

        toasts?.success({
            message: `Translation sync completed for ${environment.toUpperCase()}.`,
            overrides: {
                duration: 2 * 1000,
            },
        })
    } catch (err) {
        toasts?.error({
            message: `Translation sync failed for ${environment.toUpperCase()}.`,
            overrides: {
                duration: 2 * 1000,
            },
        })

        console.error('Failed to sync translations.', err)
    } finally {
        mainStore.isTranslationSyncing = false
    }
}

const onCopyTranslationTokenClick = async () => {
    try {
        const translationToken = await sdk?.auth?.getToken(COMMON_SCOPES.TRANSLATION_SERVICE)

        if (!translationToken) {
            toasts?.error({
                message: 'Could not retrieve translation token.',
                overrides: {
                    duration: 2 * 1000,
                },
            })

            return
        }

        await navigator.clipboard.writeText(translationToken)

        toasts?.success({
            message: 'Successfully copied translation token to clipboard!',
            overrides: {
                duration: 2 * 1000,
            },
        })

        console.log("Here's your translation token, you shouldn't share it though:", {
            translationToken,
        })
    } catch (err) {
        toasts?.error({
            message: 'Failed to write translation token to clipboard.',
            overrides: {
                duration: 2 * 1000,
            },
        })

        console.error('Failed to write to clipboard.', err)
    }
}
</script>

<style scoped lang="scss">
.up-dev-tools {
    display: flex;
    flex-direction: column;
    border-radius: 4px;
    white-space: nowrap;

    &--development {
        border: 1px solid $coode-header-dev;
    }

    &--staging {
        border: 1px solid $coode-header-uat;
    }

    &__environment {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        padding: $spacing-3xs;
        border-top-left-radius: 2px;
        border-top-right-radius: 2px;

        color: $pbl-foreground;
        font-weight: 700;
        gap: $spacing-2xs;

        &-title {
            font-size: 0.8em;
        }

        &-value {
            font-size: 0.8em;
            opacity: 0.8;
        }

        &--development {
            background-color: $coode-header-dev;
        }

        &--staging {
            background-color: $coode-header-uat;
        }
    }

    &__urls {
        display: flex;
        flex-direction: column;
        justify-content: center;

        padding: $spacing-2xs $spacing-xs;
        gap: $spacing-xs;

        &-item {
            display: flex;
            flex-direction: column;
            justify-content: flex-start;

            & > span {
                font-weight: 600;
            }

            & > a {
                color: $pbl-primary;
            }
        }
    }

    &__sync {
        display: grid;
        grid-auto-flow: column;
        grid-auto-columns: 1fr;
        gap: $spacing-2xs;

        padding: $spacing-2xs $spacing-xs;
        border-top: 1px solid $pbl-border;

        &-button {
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: $spacing-2xs;

            padding: $spacing-xs $spacing-s;
            border-radius: 4px;

            font-weight: 600;
            color: $pbl-primary;
            border: 1px solid $pbl-primary;
            background-color: $pbl-foreground;

            & pbl-icon {
                --size: 1.4em;
            }

            .up-dev-tools__sync-icon--spinning {
                animation: up-dev-tools-spin 1s linear infinite;
                transform-origin: center;
            }

            &:hover:not(&:active) {
                background-color: color-mix(in srgb, $pbl-background 90%, $pbl-text);
            }

            &:disabled {
                cursor: not-allowed;
                color: $pbl-text-muted;
                border-color: $pbl-border;
            }

        }
    }

    &__quick-links {
        display: flex;
        flex-direction: column;
        justify-content: center;

        border-top: 1px solid $pbl-border;

        &-button {
            cursor: pointer;

            display: flex;
            align-items: center;
            justify-content: center;
            gap: $spacing-xs;

            width: 100%;
            padding: $spacing-2xs $spacing-xs;

            border: 0;
            border-bottom-left-radius: 4px;
            border-bottom-right-radius: 4px;

            color: $pbl-text;
            background-color: $pbl-background;

            &:hover {
                background-color: color-mix(in srgb, $pbl-background 90%, $pbl-text);
            }

            &:active {
                background-color: $pbl-background;
            }

        }
    }
}

@keyframes up-dev-tools-spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>
